import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import { Ratelimit } from '@upstash/ratelimit'
import { Redis } from '@upstash/redis'
import { DO_RATE_LIMITING, S_DEBUG_MODE } from '../api/utils/config'
import {
  isNotNullOrEmptyString,
  toTrimmedStringLowerCase,
} from '../../app/utilities/misc'
import { UPSTASH_ENDPOINT, UPSTASH_API_KEY } from '../api/utils/config'
import {
  translate,
  SPAIN_ISO_3166_ALPHA_2_CODE,
  FRANCE_ISO_3166_ALPHA_2_CODE,
  SOUTH_KOREA_ISO_3166_ALPHA_2_CODE,
  UNITED_STATES_ISO_3166_ALPHA_2_CODE,
} from '../../app/utilities/language'
import {
  DEBUG_MODE,
  OG_SITE_URL,
  OG_SITE_NAME,
  CONTACT_EMAIL,
  JSON_LD_SITE_NAME,
  OG_SITE_URL_FRENCH,
  OG_SITE_URL_KOREAN,
  OG_SITE_URL_SPANISH,
  OG_SITE_NAME_FRENCH,
  OG_SITE_NAME_KOREAN,
  OG_SITE_NAME_SPANISH,
  JSON_LD_TYPE_ARTICLE,
  JSON_LD_URL_HOME_FRENCH,
  JSON_LD_URL_HOME_KOREAN,
  JSON_LD_URL_HOME_SPANISH,
  JSON_LD_URL_HOME_ENGLISH,
  JSON_LD_SITE_NAME_FRENCH,
  JSON_LD_SITE_NAME_KOREAN,
  formatDateForJsonLdField,
  JSON_LD_SITE_NAME_SPANISH,
  HOW_TO_BUILD_PAGE_DISPLAY_NAME,
  JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT,
  JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_FRENCH,
  JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_KOREAN,
  JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_SPANISH,
} from '../../app/utilities/config'
import useWindowDimensions from '../../app/components/hooks/useWindowDimensions'
import {
  ARTICLE_SUMMARY,
  ARTICLE_KEYWORDS,
  ARTICLE_SUMMARY_ES,
  ARTICLE_SUMMARY_FR,
  ARTICLE_SUMMARY_KO,
  ARTICLE_KEYWORDS_ES,
  ARTICLE_KEYWORDS_FR,
  ARTICLE_KEYWORDS_KO,
  OPEN_GRAPH_IMAGE_JPEG,
  HOW_TO_BUILD_ARTICLE_URL,
  HOW_TO_BUILD_ARTICLE_TITLE,
  HOW_TO_BUILD_ARTICLE_URL_ES,
  HOW_TO_BUILD_ARTICLE_URL_FR,
  HOW_TO_BUILD_ARTICLE_URL_KO,
  HOW_TO_BUILD_ARTICLE_CREATED,
  HOW_TO_BUILD_ARTICLE_TITLE_ES,
  HOW_TO_BUILD_ARTICLE_TITLE_FR,
  HOW_TO_BUILD_ARTICLE_TITLE_KO,
  HOW_TO_BUILD_ARTICLE_PUBLISHED,
  HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
} from '../../app/static-data/how-to-build-your-perfect-workplace-with-the-best-music-strategy-data'
import {
  HOME_TITLE_MARKUP_STR,
  HOME_TITLE_MARKUP_STR_ES,
  HOME_TITLE_MARKUP_STR_FR,
  HOME_TITLE_MARKUP_STR_KO,
} from '../../app/static-data/index-data'

const HowToBuild: React.ComponentType<any> = dynamic(
  () => import('../../app/components/pages/Articles/HowToBuild/HowToBuild'),
  {
    ssr: true,
  },
)

function HowToBuildPage(props: any) {
  const { width, height } =
    typeof window !== 'undefined'
      ? useWindowDimensions()
      : { width: 0, height: 0 }

  useEffect(() => {
    try {
      props?.broadcastDimensions(height, width)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [width, height])

  return (
    <HowToBuild
      isBot={props?.isBot}
      width={props?.width}
      height={props?.height}
      navigateRoute={props?.navigateRoute}
      avifCompatible={props?.avifCompatible}
      selectedLanguage={props?.selectedLanguage}
      broadcastDimensions={props?.broadcastDimensions}
    />
  )
}

const cache: Map<any, any> = new Map()
const redis: Redis = new Redis({
  url: UPSTASH_ENDPOINT,
  token: UPSTASH_API_KEY,
})
const ratelimit: any = new Ratelimit({
  redis: redis,
  limiter: Ratelimit.slidingWindow(9, '3 s'),
  ephemeralCache: cache,
})

export async function getServerSideProps(context: any) {
  let initialCountryCode: any = ''
  const initialLocale: any = context?.locale
  const initialLang: string =
    isNotNullOrEmptyString(initialLocale) &&
    initialLocale?.toString().indexOf('-') > 1
      ? initialLocale.split('-')[0]
      : initialLocale
  try {
    if (isNotNullOrEmptyString(context?.req?.headers['x-vercel-ip-country']))
      initialCountryCode = context?.req?.headers['x-vercel-ip-country']
        ?.toString()
        .trim()
    else
      initialCountryCode =
        initialLang == 'ko'
          ? SOUTH_KOREA_ISO_3166_ALPHA_2_CODE
          : initialLang == 'fr'
            ? FRANCE_ISO_3166_ALPHA_2_CODE
            : initialLang == 'es'
              ? SPAIN_ISO_3166_ALPHA_2_CODE
              : UNITED_STATES_ISO_3166_ALPHA_2_CODE
  } catch (ecs: any) {
    if (S_DEBUG_MODE) console.log(ecs)
  }
  try {
    try {
      context?.res.setHeader(
        'Cache-Control',
        'public, max-age=86400, s-maxage=86400, stale-while-revalidate=86400',
      )
      context?.res.setHeader('X-Frame-Options', 'SAMEORIGIN')
    } catch (er) {
      if (S_DEBUG_MODE) console.log(er)
    }
    let rateLimited: boolean = false
    let isBot: boolean = false
    try {
      const BOTS_USER_AGENT_LIST = (await import('../../app/utilities/bots'))
        .BOTS_USER_AGENT_LIST
      let userAgentLowerCase = context?.req?.headers['user-agent']
        ?.toString()
        .trim()
        .toLowerCase()
      if (isNotNullOrEmptyString(userAgentLowerCase))
        for (let i = 0; i < BOTS_USER_AGENT_LIST?.length; i++) {
          if (
            userAgentLowerCase.indexOf(
              toTrimmedStringLowerCase(BOTS_USER_AGENT_LIST[i]?.userAgent),
            ) > -1
          ) {
            isBot = true
            break
          }
        }
    } catch (erra) {
      if (S_DEBUG_MODE) console.log(erra)
    }
    try {
      if (DO_RATE_LIMITING && !isBot) {
        try {
          const ip = context?.req?.ip ?? '127.0.0.1'
          const { success } = await ratelimit.limit(ip)
          rateLimited = !success
        } catch (er: any) {
          if (S_DEBUG_MODE) console.log(er)
        }
        if (rateLimited) isBot = true
      }
    } catch (erra) {
      if (S_DEBUG_MODE) console.log(erra)
    }
    return {
      props: {
        initialLocale: initialLocale,
        initialLang: initialLang,
        initialCountryCode: initialCountryCode,
        canonicalUrl: translate(
          HOW_TO_BUILD_ARTICLE_URL,
          HOW_TO_BUILD_ARTICLE_URL_ES,
          HOW_TO_BUILD_ARTICLE_URL_FR,
          HOW_TO_BUILD_ARTICLE_URL_KO,
          initialLang,
        ),
        jsonLdSiteName: translate(
          JSON_LD_SITE_NAME,
          JSON_LD_SITE_NAME_SPANISH,
          JSON_LD_SITE_NAME_FRENCH,
          JSON_LD_SITE_NAME_KOREAN,
          initialLang,
        ),
        ogPageUrl: translate(
          HOW_TO_BUILD_ARTICLE_URL,
          HOW_TO_BUILD_ARTICLE_URL_ES,
          HOW_TO_BUILD_ARTICLE_URL_FR,
          HOW_TO_BUILD_ARTICLE_URL_KO,
          initialLang,
        ),
        ogSiteUrl: translate(
          OG_SITE_URL,
          OG_SITE_URL_SPANISH,
          OG_SITE_URL_FRENCH,
          OG_SITE_URL_KOREAN,
          initialLang,
        ),
        ogSiteName: translate(
          OG_SITE_NAME,
          OG_SITE_NAME_SPANISH,
          OG_SITE_NAME_FRENCH,
          OG_SITE_NAME_KOREAN,
          initialLang,
        ),
        jsonLdType: JSON_LD_TYPE_ARTICLE,
        jsonLdOrganizationDescription: translate(
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_SPANISH,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_FRENCH,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_KOREAN,
          initialLang,
        ),
        jsonLdHomeUrl: translate(
          JSON_LD_URL_HOME_ENGLISH,
          JSON_LD_URL_HOME_SPANISH,
          JSON_LD_URL_HOME_FRENCH,
          JSON_LD_URL_HOME_KOREAN,
          initialLang,
        ),
        jsonLdOrganizationContactEmail: CONTACT_EMAIL,
        jsonLdArticleHeadline: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        jsonLdArticleImage1200x1200: translate(
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          initialLang,
        ),
        jsonLdArticleDatePublished: formatDateForJsonLdField(
          HOW_TO_BUILD_ARTICLE_PUBLISHED,
        ),
        jsonLdArticleDateCreated: formatDateForJsonLdField(
          HOW_TO_BUILD_ARTICLE_CREATED,
        ),
        jsonLdArticleUrl: translate(
          HOW_TO_BUILD_ARTICLE_URL,
          HOW_TO_BUILD_ARTICLE_URL_ES,
          HOW_TO_BUILD_ARTICLE_URL_FR,
          HOW_TO_BUILD_ARTICLE_URL_KO,
          initialLang,
        ),
        jsonLdArticleInLanguage: initialLang,
        jsonLdArticleKeywords: translate(
          ARTICLE_KEYWORDS,
          ARTICLE_KEYWORDS_ES,
          ARTICLE_KEYWORDS_FR,
          ARTICLE_KEYWORDS_KO,
          initialLang,
        ),
        jsonLdTitle: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        jsonLdDescription: translate(
          ARTICLE_SUMMARY,
          ARTICLE_SUMMARY_ES,
          ARTICLE_SUMMARY_FR,
          ARTICLE_SUMMARY_KO,
          initialLang,
        ),
        ogPageImage: translate(
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          initialLang,
        ),
        titleTag: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        metaDescription: translate(
          ARTICLE_SUMMARY,
          ARTICLE_SUMMARY_ES,
          ARTICLE_SUMMARY_FR,
          ARTICLE_SUMMARY_KO,
          initialLang,
        ),
        metaKeywords: translate(
          ARTICLE_KEYWORDS,
          ARTICLE_KEYWORDS_ES,
          ARTICLE_KEYWORDS_FR,
          ARTICLE_KEYWORDS_KO,
          initialLang,
        ),
        jsonLdBlogTitle: translate(
          HOME_TITLE_MARKUP_STR,
          HOME_TITLE_MARKUP_STR_ES,
          HOME_TITLE_MARKUP_STR_FR,
          HOME_TITLE_MARKUP_STR_KO,
          initialLang,
        ),
        rl: rateLimited,
        ib: isBot,
      },
    }
  } catch (e) {
    if (S_DEBUG_MODE) console.log(e)
    return {
      props: {
        initialLocale: initialLocale,
        initialLang: initialLang,
        initialCountryCode: initialCountryCode,
        canonicalUrl: translate(
          HOW_TO_BUILD_ARTICLE_URL,
          HOW_TO_BUILD_ARTICLE_URL_ES,
          HOW_TO_BUILD_ARTICLE_URL_FR,
          HOW_TO_BUILD_ARTICLE_URL_KO,
          initialLang,
        ),
        jsonLdSiteName: translate(
          JSON_LD_SITE_NAME,
          JSON_LD_SITE_NAME_SPANISH,
          JSON_LD_SITE_NAME_FRENCH,
          JSON_LD_SITE_NAME_KOREAN,
          initialLang,
        ),
        ogPageUrl: translate(
          HOW_TO_BUILD_ARTICLE_URL,
          HOW_TO_BUILD_ARTICLE_URL_ES,
          HOW_TO_BUILD_ARTICLE_URL_FR,
          HOW_TO_BUILD_ARTICLE_URL_KO,
          initialLang,
        ),
        ogSiteUrl: translate(
          OG_SITE_URL,
          OG_SITE_URL_SPANISH,
          OG_SITE_URL_FRENCH,
          OG_SITE_URL_KOREAN,
          initialLang,
        ),
        ogSiteName: translate(
          OG_SITE_NAME,
          OG_SITE_NAME_SPANISH,
          OG_SITE_NAME_FRENCH,
          OG_SITE_NAME_KOREAN,
          initialLang,
        ),
        jsonLdType: JSON_LD_TYPE_ARTICLE,
        jsonLdOrganizationDescription: translate(
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_SPANISH,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_FRENCH,
          JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT_KOREAN,
          initialLang,
        ),
        jsonLdHomeUrl: translate(
          JSON_LD_URL_HOME_ENGLISH,
          JSON_LD_URL_HOME_SPANISH,
          JSON_LD_URL_HOME_FRENCH,
          JSON_LD_URL_HOME_KOREAN,
          initialLang,
        ),
        jsonLdOrganizationContactEmail: CONTACT_EMAIL,
        jsonLdArticleHeadline: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        jsonLdArticleImage1200x1200: translate(
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
          initialLang,
        ),
        jsonLdArticleDatePublished: formatDateForJsonLdField(
          HOW_TO_BUILD_ARTICLE_PUBLISHED,
        ),
        jsonLdArticleDateCreated: formatDateForJsonLdField(
          HOW_TO_BUILD_ARTICLE_CREATED,
        ),
        jsonLdTitle: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        jsonLdDescription: translate(
          ARTICLE_SUMMARY,
          ARTICLE_SUMMARY_ES,
          ARTICLE_SUMMARY_FR,
          ARTICLE_SUMMARY_KO,
          initialLang,
        ),
        ogPageImage: translate(
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          OPEN_GRAPH_IMAGE_JPEG,
          initialLang,
        ),
        titleTag: translate(
          HOW_TO_BUILD_ARTICLE_TITLE,
          HOW_TO_BUILD_ARTICLE_TITLE_ES,
          HOW_TO_BUILD_ARTICLE_TITLE_FR,
          HOW_TO_BUILD_ARTICLE_TITLE_KO,
          initialLang,
        ),
        metaDescription: translate(
          ARTICLE_SUMMARY,
          ARTICLE_SUMMARY_ES,
          ARTICLE_SUMMARY_FR,
          ARTICLE_SUMMARY_KO,
          initialLang,
        ),
        metaKeywords: translate(
          ARTICLE_KEYWORDS,
          ARTICLE_KEYWORDS_ES,
          ARTICLE_KEYWORDS_FR,
          ARTICLE_KEYWORDS_KO,
          initialLang,
        ),
        jsonLdBlogTitle: translate(
          HOME_TITLE_MARKUP_STR,
          HOME_TITLE_MARKUP_STR_ES,
          HOME_TITLE_MARKUP_STR_FR,
          HOME_TITLE_MARKUP_STR_KO,
          initialLang,
        ),
        rl: false,
        ib: false,
      },
    }
  }
}

HowToBuildPage.displayName = HOW_TO_BUILD_PAGE_DISPLAY_NAME

export default HowToBuildPage
